/* global FavoritesEnabled */
import _ from 'lodash';
import {
  PageNames,
  handleTealiumButtonAction,
  SegmentSources,
  tealiumEventPageTypes,
} from '@foxcorp/delta-web-analytics';
import {
  formatAiredDate,
  formatMovieMetadata,
  formatSeasonEpisode,
  formatSeasonEpisodeExtended,
} from 'helpers/VideoHelper';
import {
  formatUpcomingDate,
  getDisplayBrand,
  getNetworkLogoUrl,
  getRequiresAuth,
  getShowType,
  isClip,
  isEpisode,
  isLive,
  isLiveVideoExpired,
  isMovie,
  isSpecial,
  isSports,
  isStaticRender,
  isTypeSeries,
  isTypeVideo,
  isUpcomingContent,
  PANEL_TYPE_VIDEOS,
} from 'helpers/ContentHelper';
import { FORMAT_TIME } from 'helpers/DateTimeHelper';
import { getOptimizelyFakeCollectionData } from 'helpers/OptimizelyHelper';
import {
  TYPE_SERIES,
  homeVideoProcessor,
  getItemBadge,
  getVideoObjectImage,
  getVideoObjectImages,
  titleElement,
  getHomeSubtitleText,
  subtitleElement,
} from '../VideoProcessorHelper';
import {
  helperGetBaseUrlSlug,
  helperGetRelativeUrl,
  helperGetStaticContentUrl,
} from '../RouteHelper';

function getFormattedVideoData(images, prioritizedNetwork) {
  const defaultImage = 'videoList';
  const imageObj = getVideoObjectImages({ images, defaultImage });
  const image = getVideoObjectImage(imageObj, prioritizedNetwork);
  return image;
}

function getTitle({ seriesName, name, type, requiresAuth, theme, isAuthenticated }) {
  const seriesTitle = name;
  const videoTitle = `${seriesName} - ${name}`;
  const title = isTypeVideo(type) ? videoTitle : seriesTitle;
  const element = titleElement({ title, requiresAuth, theme, isAuthenticated });
  return element;
}

export function CarouselVideoProcessor(video) {
  if (!video) return null;
  const {
    '@id': id,
    '@type': type,
    images,
    name,
    seriesName,
    playerScreenUrl,
    description,
    theme,
    percentWatched,
    isAuthenticated,
    screenUrl,
  } = video;

  const requiresAuth = getRequiresAuth({ video });
  const isVideo = isTypeVideo(type);
  const isSeries = TYPE_SERIES === type;

  const title = getTitle({ seriesName, name, type, requiresAuth, theme, isAuthenticated });
  const displayBrand = getDisplayBrand(video);
  const networkIcon = getNetworkLogoUrl(displayBrand);
  const image = getFormattedVideoData(images, displayBrand);
  const badge = getItemBadge(video);

  const url = isVideo ? playerScreenUrl : screenUrl;
  const link = helperGetRelativeUrl({ url });

  const processedVideoData = {
    id,
    link,
    title,
    description,
    image,
    playButton: false,
    trackButtonClick: handleTealiumButtonAction,
    badge,
    networkIcon,
    percentWatched,
    descriptionLines: 3,
    preventLazyLoad: false,
    toggleEnabled: false,
  };

  const processedSeriesData = {
    id,
    link,
    image,
    playButton: false,
    description: null,
    trackButtonClick: handleTealiumButtonAction,
    badge,
    networkIcon,
    percentWatched,
    preventLazyLoad: true,
    toggleEnabled: false,
  };

  if (isVideo) return processedVideoData;
  if (isSeries) return processedSeriesData;
  return null;
}

export function homeLinkTileCarouselProcessor(item) {
  if (!item) return null;

  const { id, images, headline, link: linkData } = item;

  const imageUrl = _.get(images, 'seriesDetail');
  const image = imageUrl && `${imageUrl}`;
  const url = _.get(linkData, '0.url');
  const baseUrlSlug = helperGetBaseUrlSlug();
  const link = `${baseUrlSlug}${url?.toLowerCase()}`;
  const title = titleElement({
    title: headline,
    hideLockIcon: true,
  });

  return {
    id,
    image,
    link,
    tileType: 'linkTile',
    title,
    titleLink: link,
  };
}

export function homeVideoOverlayProcessor(video, processedProps) {
  const {
    '@type': type,
    categoryType,
    contentLabel,
    contentRating,
    description: originalDescription,
    durationInSeconds,
    endDate,
    episodeNumber,
    favoritableItems,
    fullEpisodeCount,
    isSeriesDetailAvailable,
    livePlayerScreenUrl,
    name,
    network,
    playerScreenUrl,
    releaseYear,
    seasonCount,
    seasonNumber,
    seriesName,
    seriesScreenUrl,
    seriesType,
    showCode,
    sportTag,
    startDate,
    subRatings,
    trackingData,
    videoType,
  } = video;
  const { seriesPlayerScreenUrl, seriesSeasonEpisode } = processedProps;
  const hasNetworkLogo = !!window.ExternalConfig.networkLogo;
  const videoIsClip = isClip(videoType);
  const videoIsLive = isLive(type);
  const videoIsUpcoming = isUpcomingContent(video);
  const videoIsEpisode = isEpisode(videoType);
  const videoIsSports = isSports(seriesType);
  const videoIsSpecial = isSpecial(seriesType);
  const videoIsSeries = isTypeSeries(type);
  const videoIsMovie = isMovie(videoType);
  const videoIsContinueWatching =
    contentLabel && _.toLower(contentLabel[0]) === 'continue watching';
  const description = originalDescription || null;
  const displayBrand = getDisplayBrand(video);
  const networkLogoUrl = hasNetworkLogo ? getNetworkLogoUrl(displayBrand) : null;
  const displaySeasonAndEpisodeCounts = _.get(video, 'displaySeasonAndEpisodeCounts', true);
  let badge = getItemBadge(video);
  let title = name;
  let subtitle = null;
  let upcomingDate = null;
  let metadata = null;

  if (videoIsClip && seriesName) {
    subtitle = seriesName;
  } else if (videoIsEpisode) {
    title = seriesName;
    if (videoIsSports) {
      subtitle = sportTag;
    } else if (!videoIsSpecial && displaySeasonAndEpisodeCounts) {
      const seasonEpisodeDisplay = formatSeasonEpisode(seasonNumber, episodeNumber);
      subtitle = `${seasonEpisodeDisplay}${name}`;
    }
  }

  if (videoIsUpcoming) {
    upcomingDate = formatUpcomingDate(startDate);
    metadata = formatMovieMetadata({
      releaseYear: formatAiredDate(startDate, ''),
      contentRating,
      subRatings,
      durationInSeconds,
    });
    badge = null;
  } else if (videoIsLive) {
    metadata = formatMovieMetadata({
      releaseYear: formatAiredDate(startDate, '', FORMAT_TIME),
      durationInSeconds,
    });
  } else if (videoIsSeries) {
    const currentSeasonNumberFallback = _.get(video, 'oldestEpisode.seasonNumber', null);
    const currentSeasonNumber = _.get(
      video,
      'currentSeason.seasonNumber',
      currentSeasonNumberFallback,
    );
    metadata = formatSeasonEpisodeExtended(seasonCount, currentSeasonNumber, fullEpisodeCount);
  } else if (videoIsMovie) {
    metadata = formatMovieMetadata({
      releaseYear,
      contentRating,
      subRatings,
      durationInSeconds,
    });
  }

  let showPlayButton = true;
  if (
    videoIsUpcoming ||
    (videoIsLive && (!livePlayerScreenUrl || isLiveVideoExpired(endDate))) ||
    (videoIsSeries && !seriesPlayerScreenUrl) ||
    (!videoIsLive && !videoIsSeries && !playerScreenUrl)
  )
    showPlayButton = false;

  const {
    global_continueWatchingButton: continueWatchingText,
    global_playLabel: playText,
    global_watchClipButton: watchClipText,
    global_watchLiveButton: watchLiveText,
    global_watchNowButton: watchNowText,
  } = window.Language;

  let playButtonText = null;
  if (showPlayButton) {
    if (videoIsLive) {
      playButtonText = watchLiveText;
    } else if (videoIsContinueWatching) {
      playButtonText = continueWatchingText;
    } else if (videoIsClip) {
      playButtonText = watchClipText;
    } else if (videoIsSeries && seriesSeasonEpisode && displaySeasonAndEpisodeCounts) {
      playButtonText = `${playText} ${seriesSeasonEpisode}`;
    } else {
      playButtonText = watchNowText;
    }
  }

  let favoriteData = null;
  if (FavoritesEnabled) {
    const showType = getShowType({ videoType, seriesType });
    const isPersonality = categoryType && categoryType === 'personality';
    const recommendationId = _.get(trackingData, 'properties.recommendationId', '');
    favoriteData = {
      actionLocation: 'playlist',
      favoritableItems,
      favoriteType: isPersonality ? 'personality' : showType,
      location: _.get(tealiumEventPageTypes, PageNames.HOME),
      networkId: network,
      recommendationId,
      seriesName: seriesName || name,
      showID: showCode,
      showType,
      source: SegmentSources.DETAIL,
    };
  }

  return {
    badge,
    description,
    favoriteData,
    metadata,
    network,
    networkLogoUrl,
    isSeriesDetailAvailable,
    playButtonText,
    seriesScreenUrl,
    showPlayButton,
    subtitle,
    title,
    upcomingDate,
  };
}

export function homeCarouselVideoProcessor({
  account,
  bookmarks,
  collectionType,
  displayTemplate,
  isHoverStateV2,
  isMobile,
  isPosters,
  overridePlayButtonClick,
  panelType,
  video,
}) {
  const {
    '@type': type,
    categoryType,
    id,
    isAuthenticated,
    isNationStubData,
    isSeriesDetailAvailable,
    isUserOutOfMarket,
    livePlayerScreenUrl,
    name,
    playerScreenUrl,
    seriesName,
    seriesScreenUrl,
    theme,
  } = video;

  if (type === 'seeMore') return homeLinkTileCarouselProcessor(video);
  const isVideo = isTypeVideo(type);
  const videoIsLive = isLive(type);
  const subtitleText = getHomeSubtitleText(video);
  let processedProps = homeVideoProcessor({
    account,
    bookmarks,
    isHoverStateV2,
    isPosters,
    item: video,
  });

  const requiresAuth = _.get(processedProps, 'requiresAuth');
  const videoTitle = titleElement({
    title: seriesName || name,
    requiresAuth,
    theme,
    isAuthenticated,
    hideLockIcon: false,
    isHoverStateV2,
    video,
  });
  const title = isVideo || videoIsLive ? videoTitle : processedProps.title;

  let timeSinceAired;

  let videosSubtitleText = subtitleText === processedProps.title ? '' : subtitleText;

  if (isVideo || videoIsLive) {
    videosSubtitleText = subtitleText === (seriesName || name) ? '' : subtitleText;
  }

  const videosSubtitle = subtitleElement(videosSubtitleText, timeSinceAired);
  const subtitle = isVideo ? videosSubtitle : processedProps.subtitle;

  processedProps = {
    ...processedProps,
    isSeriesDetailAvailable,
    isUserOutOfMarket,
    livePlayerScreenUrl,
    originalItem: video,
    overridePlayButtonClick,
    preventLazyLoad: true,
    subtitleText,
    toggleEnabled: true,
  };

  const { playButton } = processedProps;
  const isPersonality = categoryType === 'personality';
  let showPlayHoverIcon = playButton && !isPersonality && !isUpcomingContent(video);
  let overlayProps = null;
  let posterImage = _.get(video, 'images.dcg-mark-poster.raw', null);
  const posterAltText = _.get(video, 'images.dcg-mark-poster.altText', '');
  showPlayHoverIcon = false;
  overlayProps = homeVideoOverlayProcessor(video, processedProps);
  overlayProps.isPersonality = isPersonality;
  let videoPosterImage = _.get(video, 'images.video-poster');

  if (typeof videoPosterImage === 'object') {
    videoPosterImage = _.get(video, 'images.video-poster.raw');
  }

  if (panelType === PANEL_TYPE_VIDEOS) {
    posterImage = videoPosterImage || posterImage;
  }
  const fallbackPoster = helperGetStaticContentUrl('images', 'FallBack-FOXNATION-Poster.jpg');
  posterImage = !posterImage ? fallbackPoster : posterImage;

  processedProps = {
    ...processedProps,
    hasOverlay: !isMobile, // adds overlay for posters except mobile
    itemIndex: id,
    metadata: null,
    overlayProps,
    playButton: showPlayHoverIcon,
    playerScreenUrl,
    posterImage,
    posterAltText,
    preventToggle: true, // hides description
    requiresAuth,
    seriesScreenUrl,
    subtitle,
    title,
    toggleEnabled: false,
  };

  if (isNationStubData) {
    const fakeCollecitonData = getOptimizelyFakeCollectionData();
    processedProps.isRedirect = true;
    processedProps.link = fakeCollecitonData.collectionLink || 'https://nation.foxnews.com/';
  }
  if (isStaticRender() || isSeriesDetailAvailable === false) {
    processedProps.titleLink = null;
    if (isUpcomingContent(video)) {
      processedProps.link = null;
    }
  }
  return processedProps;
}

export function networkUpsellHeroCarouselProcessor(item) {
  const {
    body,
    ctaText,
    onClick,
    description,
    externalLink,
    header,
    id,
    image,
    itemIndex,
    metadata,
    slideIndex,
    url,
    video,
  } = item;

  const ctaData = [
    {
      text: ctaText,
      url,
      onClick,
      externalLink,
    },
  ];

  const processedData = {
    ctaData,
    ctaPosition: 'middle',
    description,
    id,
    image,
    metadata,
    playButton: false,
    preventLazyLoad: true,
    subtitle: body,
    title: header,
    toggleEnabled: false,
    video,
    videoPlay: itemIndex === slideIndex,
  };

  return processedData;
}
